import React, { createContext, useState, useEffect } from 'react';
import {
    accessoryIsRacing,
    buildEcommerceItem,
    getBrowseContext,
    inferAddToCartSource,
    pushEcommerceEvent,
} from '../utils/dataLayer';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
    const [cart, setCart] = useState(() => {
        try {
            const savedCart = localStorage.getItem('cart');
            return savedCart ? JSON.parse(savedCart) : {};
        } catch (error) {
            console.error('Error parsing cart from localStorage:', error);
            // Clear corrupted cart data
            localStorage.removeItem('cart');
            return {};
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem('cart', JSON.stringify(cart));
        } catch (error) {
            console.error('Error saving cart to localStorage:', error);
            // If localStorage is full or corrupted, try to clear it and retry
            try {
                localStorage.removeItem('cart');
                localStorage.setItem('cart', JSON.stringify(cart));
            } catch (retryError) {
                console.error('Failed to save cart even after clearing localStorage:', retryError);
            }
        }
    }, [cart]);

    const addToCart = (item, quantity, options = {}) => {
        console.log('🛒 CartContext.addToCart called');
        console.log('🛒 Item:', item);
        console.log('🛒 Quantity:', quantity);
        
        if (!item || !item.id) {
            console.error('❌ CartContext: Invalid item provided', item);
            return;
        }

        const qty = parseInt(quantity, 10) || 1;
        
        setCart(prevCart => {
            console.log('🛒 Previous cart:', prevCart);
            const updatedCart = { ...prevCart };
            const previousQuantity = updatedCart[item.id]?.quantity || 0;
            const newQuantity = previousQuantity + qty;
            
            updatedCart[item.id] = {
                id: item.id,
                quantity: newQuantity,
                category_id: (item.category_id !== undefined && item.category_id !== '') ? String(item.category_id) : (localStorage.getItem('category_id') || '')
            };
            
            console.log('🛒 Updated cart:', updatedCart);
            console.log('🛒 Item quantity:', previousQuantity, '->', newQuantity);
            
            // Update local storage
            localStorage.setItem('cart', JSON.stringify(updatedCart));
            console.log('🛒 Cart saved to localStorage');
            
            return updatedCart;
        });

        const source = options.source || inferAddToCartSource();
        const itemListName = options.item_list_name || source;
        const ecommerceItem = buildEcommerceItem(item, {
            quantity: qty,
            item_list_name: itemListName,
            item_category: options.item_category || item.item_category,
        });
        const unitPrice = ecommerceItem.price != null ? Number(ecommerceItem.price) : 0;
        pushEcommerceEvent('add_to_cart', {
            // ui_source (not "source") — GTM was mapping dataLayer.source onto sessionSource
            ui_source: source,
            item_list_name: itemListName,
            accessory_id: String(item.id),
            item_id: String(item.id),
            is_racing: accessoryIsRacing(item),
            currency: item.currency,
            value: unitPrice * qty,
            ...getBrowseContext(),
            ecommerce: {
                currency: item.currency,
                value: unitPrice * qty,
                items: [ecommerceItem],
            },
        });
    };

    const updateQuantity = (id, quantity) => {
        setCart(prevCart => ({
            ...prevCart,
            [id]: { ...prevCart[id], quantity }
        }));
    };

    const removeFromCart = (id, options = {}) => {
        setCart(prevCart => {
            const newCart = { ...prevCart };
            delete newCart[id];
            return newCart;
        });
        if (options.silent) return;
        const item = options.item || { id };
        const source = options.source || inferAddToCartSource();
        pushEcommerceEvent('remove_from_cart', {
            ui_source: source,
            is_racing: accessoryIsRacing(item),
            ecommerce: {
                currency: item.currency || item.accessory?.currency,
                items: [buildEcommerceItem(item.accessory || item, {
                    quantity: item.quantity || 1,
                    item_id: String(id),
                })],
            },
        });
    };

    const clearCart = () => {
        setCart({});
    }

    return (
        <CartContext.Provider value={{ cart, addToCart, updateQuantity, removeFromCart, clearCart }}>
            {children}
        </CartContext.Provider>
    );
};
